import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { lenisRef } from "@/lib/lenis";
import { ArrowRight, Download, Share2, Check } from "lucide-react";
import html2canvas from "html2canvas";
import { Reveal, SectionHeading, EASE } from "@/components/Reveal";
import SpinView from "@/components/SpinView";

const IMG = {
  rear: "/images/aero-rear.jpg",
  cockpit: "/images/panel-interior.jpg",
  seats: "/images/seats.jpg",
  suv: "/images/panel-power.jpg",
  offroad: "/images/panel-offroad.jpg",
  highway: "/images/suv-offroad.jpg",
  hero: "/images/suv-forest.jpg",
  detail: "/images/seats.jpg",
};

const GALLERY_MODELS = [
  { id: "aero", label: "AERO D" },
  { id: "suv", label: "SUV" },
  { id: "electric", label: "ELECTRIC" },
];

const MODEL_COLORS = {
  aero: [
    { id: "black", name: "Obsidian Black", hex: "#0f0f14", glow: "rgba(150,160,180,0.22)", img: "/images/aero-black.jpg" },
    { id: "white", name: "Pearl White", hex: "#e8e9ed", glow: "rgba(220,225,235,0.25)", img: "/images/aero-white.jpg" },
    { id: "silver", name: "Liquid Silver", hex: "#c7c9d1", glow: "rgba(200,210,230,0.28)", img: "/images/aero-silver.jpg" },
  ],
  suv: [
    { id: "green", name: "Forest Green", hex: "#5e6b4a", glow: "rgba(160,170,130,0.25)", img: "/images/suv-forest.jpg" },
    { id: "black", name: "Midnight Black", hex: "#0f0f14", glow: "rgba(150,160,180,0.22)", img: "/images/suv-midnight.jpg" },
    { id: "white", name: "Glacier White", hex: "#e8e9ed", glow: "rgba(220,225,235,0.25)", img: "/images/suv-glacier.jpg" },
    { id: "sand", name: "Desert Sand", hex: "#c2a878", glow: "rgba(230,200,140,0.25)", img: "/images/suv-sand.jpg" },
    { id: "grey", name: "Steel Grey", hex: "#8b9096", glow: "rgba(180,190,200,0.25)", img: "/images/suv-steel.jpg" },
    { id: "matte", name: "Matte Black", hex: "#1c1c1e", glow: "rgba(120,120,130,0.22)", img: "/images/suv-matte.jpg" },
  ],
  electric: [
    { id: "white", name: "Pearl White", hex: "#e8e9ed", glow: "rgba(220,225,235,0.25)", img: "/images/ev-hero.jpg" },
    { id: "black", name: "Obsidian Black", hex: "#0f0f14", glow: "rgba(150,160,180,0.22)", img: "/images/ev-black.jpg" },
    { id: "silver", name: "Liquid Silver", hex: "#c7c9d1", glow: "rgba(200,210,230,0.28)", img: "/images/ev-silver.jpg" },
  ],
};

const VARIANT_POSTERS = [
  { id: "one", no: "01", name: "AERO D ONE", tier: "Premium", line: "हर रोज़ की प्रीमियम ड्राइव", price: "₹ 12.74 Lakh*", was: "₹ 14.99 Lakh*", img: "/images/aero-white.jpg",
    specs: ["2.0L Diesel · RWD", "7-Seater Premium Cabin", "Dual-Screen Cockpit", "Automatic + Manual", "6 Airbags"] },
  { id: "pro", no: "02", name: "AERO D PRO", tier: "Performance", line: "जब ताक़त बोलती है", price: "₹ 16.99 Lakh*", was: "₹ 19.99 Lakh*", img: "/images/aero-black.jpg",
    specs: ["2.0L Diesel · AWD", "Sport-Tuned Suspension", "19-inch Alloy Wheels", "Paddle Shifters", "360° Camera"] },
  { id: "x", no: "03", name: "AERO D X", tier: "Adventure", line: "हर रास्ता अब आपका है", price: "₹ 21.24 Lakh*", was: "₹ 24.99 Lakh*", img: "/images/aero-silver.jpg",
    specs: ["2.0L Diesel · 4x4", "Terrain Response Modes", "Roof Rails + Cladding", "All-Terrain Tyres", "Underbody Protection"] },
  { id: "luxe", no: "04", name: "AERO D LUXE", tier: "Luxury", line: "लग्ज़री की नई परिभाषा", price: "₹ 25.49 Lakh*", was: "₹ 29.99 Lakh*", img: "/images/aero-rear.jpg",
    specs: ["3.0L Diesel · AWD", "Panoramic Roof", "Ventilated Seats", "Premium Audio System", "Full ADAS Suite"] },
];

const FRAMES = [
  { img: IMG.rear, title: "Bold Rear Design", sub: "हर मोड़ पर एक नई पहचान — LED light bar, dual exhaust look.", span: "sm:col-span-2" },
  { img: IMG.cockpit, title: "Next-Gen Interior", sub: "Technology meets luxury.", span: "" },
  { img: IMG.seats, title: "Spacious 7 Seater", sub: "Comfort for every journey.", span: "" },
  { img: IMG.offroad, title: "Off-Road · Water Wading", sub: "कहीं भी, कभी भी — built to explore.", span: "sm:col-span-2" },
  { img: IMG.highway, title: "Highway Glide", sub: "Effortless and powerful.", span: "" },
  { img: "/images/suv-side.jpg", title: "IshuCar SUV", sub: "Power Meets Purpose.", span: "" },
];

const SUV_POSTERS = [
  { id: "suv-explorer", no: "01", name: "SUV EXPLORER", tier: "Essential 4x4 AWD", line: "असली SUV का असली मज़ा", price: "₹ 50 Lakh*", was: null, img: "/images/suv-forest.jpg",
    specs: ["2.0L Diesel · 200 HP · 4x4 AWD", "Terrain Response Modes", "Premium Interior", "360° Camera + Off-Road View", "Wireless CarPlay & Android Auto"] },
  { id: "suv-adventure", no: "02", name: "SUV ADVENTURE", tier: "Advanced Off-Road Pack", line: "पहाड़ों पर भारी पड़ता है", price: "₹ 55 Lakh*", was: null, img: "/images/suv-offroad.jpg",
    specs: ["2.0L Diesel · 300 HP · 4x4 AWD", "Air Suspension (Adjustable Height)", "Panoramic Sunroof", "Ventilated & Heated Seats", "All-Terrain Tyres + Cladding"] },
  { id: "suv-signature", no: "03", name: "SUV SIGNATURE", tier: "Full Luxury", line: "लग्ज़री का ऑफ-रोड चेहरा", price: "₹ 60 Lakh*", was: null, img: "/images/suv-matte.jpg",
    specs: ["3.0L Diesel · 500 HP · 4x4 AWD", "Full ADAS Suite", "Premium Sound System", "12.3\" + 15\" Dual Display", "Towing up to 3,500 kg"] },
];

const SPECS = [
  { value: "2.0L / 3.0L", label: "Diesel engine" },
  { value: "RWD / AWD", label: "All terrain ready" },
  { value: "7 Seater", label: "Spacious cabin" },
  { value: "360°", label: "Safety with ADAS" },
];

export default function Gallery() {
  const [carModel, setCarModel] = useState("suv");
  const [color, setColor] = useState(MODEL_COLORS.suv[0]);
  const [posterModel, setPosterModel] = useState("aero");
  const [sharedId, setSharedId] = useState(null);
  const location = useLocation();

  const switchModel = (id) => {
    setCarModel(id);
    setColor(MODEL_COLORS[id][0]);
  };

  useEffect(() => {
    if (location.hash !== "#variant-posters") return;
    const t = setTimeout(() => {
      const el = document.getElementById("variant-posters");
      if (!el) return;
      if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -80 });
      else el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => clearTimeout(t);
  }, [location]);

  const handleDownload = async (p) => {
    const node = document.getElementById(`poster-card-${p.id}`);
    if (!node) return;
    try {
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#070709", useCORS: true });
      const link = document.createElement("a");
      link.download = `IshuCar-${p.name.replace(/\s+/g, "-")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      console.error("poster download failed", e);
    }
  };

  const handleShare = async (p) => {
    const url = `${window.location.origin}/gallery#variant-posters`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `IshuCar ${p.name}`, text: `IshuCar ${p.name} — ${p.tier} · ${p.price}`, url });
        return;
      }
    } catch (e) {
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setSharedId(p.id);
      setTimeout(() => setSharedId(null), 1800);
    } catch (e) {
      console.error("share failed", e);
    }
  };

  return (
    <>
      <section className="pt-36 pb-10" data-testid="gallery-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Gallery"
            title="Every angle, an argument"
            sub="Studio photography of the IshuCar — light, shadow and a silhouette built to be remembered."
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8" data-testid="color-configurator">
        <Reveal className="mb-8 flex justify-center">
          <div className="inline-flex rounded-full border border-white/10 bg-[#101014] p-1" data-testid="configurator-model-tabs">
            {GALLERY_MODELS.map((m) => (
              <button
                key={m.id}
                onClick={() => switchModel(m.id)}
                className={`rounded-full px-5 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 sm:px-6 ${
                  carModel === m.id ? "bg-[#e6b800] font-bold text-black" : "text-neutral-400 hover:text-white"
                }`}
                data-testid={`configurator-model-tab-${m.id}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0e]">
          <motion.div
            className="absolute inset-0 blur-[110px]"
            animate={{ background: `radial-gradient(ellipse at 50% 60%, ${color.glow}, transparent 65%)` }}
            transition={{ duration: 0.7 }}
            aria-hidden="true"
          />
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.img
                key={`${carModel}-${color.id}`}
                src={color.img}
                alt={`IshuCar in ${color.name}`}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: EASE }}
                className="h-[300px] w-full object-cover sm:h-[520px]"
                data-testid="configurator-car-image"
              />
            </AnimatePresence>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#070709] via-[#070709]/50 to-transparent p-6 sm:p-9">
              <motion.p
                key={color.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="font-display text-xl font-bold uppercase text-white sm:text-2xl"
                data-testid="configurator-color-name"
              >
                {color.name}
              </motion.p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {MODEL_COLORS[carModel].map((c) => (
            <button
              key={c.id}
              data-testid={`gallery-color-${c.id}`}
              onClick={() => setColor(c)}
              aria-label={`Select ${c.name}`}
              className={`group flex items-center gap-3 rounded-full border py-2 pl-2 pr-5 transition-all duration-200 ${
                color.id === c.id
                  ? "border-[#e6b800] bg-[#e6b800]/10"
                  : "border-white/15 hover:border-white/40"
              }`}
            >
              <span
                className="h-7 w-7 rounded-full border border-white/20"
                style={{ background: c.hex }}
              />
              <span className={`font-mono2 text-[11px] uppercase tracking-[0.15em] ${color.id === c.id ? "text-[#e6b800]" : "text-neutral-400"}`}>
                {c.name}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0a0a0e] py-20 lg:py-28" data-testid="gallery-specs">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-5 sm:px-8 lg:grid-cols-4">
          {SPECS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.07} className="text-center">
              <p className="text-gold-gradient font-display text-3xl font-extrabold sm:text-4xl">{s.value}</p>
              <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-neutral-500">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="gallery-frames">
        <SectionHeading eyebrow="Spotlight" title="The frames" sub="Clipped, lit and shot like the machine deserves." />
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {FRAMES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 2) * 0.08} className={f.span}>
              <div className="group relative overflow-hidden rounded-2xl border border-white/10" data-testid={`gallery-frame-${i}`}>
                <img
                  src={f.img}
                  alt={f.title}
                  loading="lazy"
                  className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-80"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#070709]/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <h3 className="font-display text-lg font-bold uppercase text-white">{f.title}</h3>
                  <p className="mt-1 text-sm text-neutral-400">{f.sub}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12 text-center" testId="gallery-cta">
          <Link
            to="/booking"
            data-testid="gallery-reserve-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-8 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_40px_rgba(230,184,0,0.5)]"
          >
            Reserve yours in {color.name}
            <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-10 sm:px-8" data-testid="gallery-spin">
        <SpinView
          heading={{
            eyebrow: "360° Studio",
            title: "घुमाकर देखिए — तीनों cars",
            sub: "AERO D, SUV या Electric — car पर drag कीजिए और हर angle से अपनी IshuCar का जादू देखिए।",
          }}
        />
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" id="variant-posters" data-testid="variant-posters-section">
        <SectionHeading
          eyebrow="Variant Posters"
          title="चारों variants का पूरा detail"
          sub="ONE से LUXE तक — हर variant की अपनी पोस्टर, अपने specs और अपनी price के साथ।"
        />
        <Reveal className="mt-8 flex justify-center">
          <div className="inline-flex rounded-full border border-white/10 bg-[#101014] p-1" data-testid="poster-model-tabs">
            {[
              { id: "aero", label: "AERO D" },
              { id: "suv", label: "SUV" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setPosterModel(m.id)}
                className={`rounded-full px-6 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                  posterModel === m.id ? "bg-[#e6b800] font-bold text-black" : "text-neutral-400 hover:text-white"
                }`}
                data-testid={`poster-tab-${m.id}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(posterModel === "aero" ? VARIANT_POSTERS : SUV_POSTERS).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08} className="h-full">
              <div
                id={`poster-card-${p.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#101014] transition-all duration-300 hover:-translate-y-2 hover:border-[#e6b800]/50"
                data-testid={`variant-poster-${p.id}`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={p.img}
                    alt={`IshuCar ${p.name}`}
                    loading="lazy"
                    className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 font-mono2 text-[10px] tracking-[0.3em] text-white/90">{p.no}</span>
                  <span className="absolute right-4 top-4 rounded-full bg-[#e6b800] px-2.5 py-1 font-mono2 text-[9px] font-bold uppercase tracking-[0.18em] text-black">
                    {p.tier}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-display text-xl font-extrabold uppercase text-white">{p.name}</h3>
                  <p className="mt-0.5 text-xs text-neutral-400">{p.line}</p>
                  <ul className="mt-4 space-y-1.5">
                    {p.specs.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-[13px] text-neutral-300">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#e6b800]" />
                        {s}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex items-end justify-between gap-2 border-t border-white/10 pt-4">
                    <div className="min-w-0">
                      <p className="whitespace-nowrap font-display text-sm font-bold text-white" data-testid={`variant-poster-price-${p.id}`}>
                        {p.was && <s className="mr-1 text-[10px] font-normal text-neutral-500">{p.was}</s>}{p.price}
                      </p>
                      <p className="whitespace-nowrap text-[9px] uppercase tracking-[0.12em] text-neutral-500">
                        Ex-showroom{posterModel !== "suv" ? " · 15% OFF" : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleDownload(p)}
                        title="Download poster"
                        data-testid={`variant-poster-download-${p.id}`}
                        className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-neutral-300 transition-colors duration-200 hover:border-[#e6b800] hover:text-[#e6b800]"
                      >
                        <Download size={15} />
                      </button>
                      <button
                        onClick={() => handleShare(p)}
                        title="Share poster"
                        data-testid={`variant-poster-share-${p.id}`}
                        className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-neutral-300 transition-colors duration-200 hover:border-[#e6b800] hover:text-[#e6b800]"
                      >
                        {sharedId === p.id ? <Check size={15} className="text-[#e6b800]" /> : <Share2 size={15} />}
                      </button>
                      <Link
                        to="/booking"
                        className="rounded-full border border-[#e6b800]/40 px-4 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#e6b800] transition-colors duration-200 hover:bg-[#e6b800] hover:text-black"
                        data-testid={`variant-poster-reserve-${p.id}`}
                      >
                        Reserve
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-[11px] leading-relaxed text-neutral-600" data-testid="variant-posters-note">
          *Ex-showroom price, indicative only — insurance, RTO और अन्य शुल्क अलग से लगेंगे।
        </p>
      </section>
    </>
  );
}